import React from "react";

import ChoiceControl from "../ChoiceControl/ChoiceControl";
import type {
  StudentAssignmentTest,
  StudentTestAttempt,
} from "../../services/studentPortalService/studentPortalService.types";
import {
  AttachmentLink,
  Choice,
  Choices,
  Question,
  QuestionComment,
  QuestionMeta,
  QuestionText,
  ResultDetails,
  ResultPercent,
  ResultState,
  ResultSummary,
  SelectedAnswer,
  TestStack,
} from "./StudentAssignmentTest.styles";

interface StudentAssignmentTestProps {
  test: StudentAssignmentTest;
  selections: Record<number, number[]>;
  onChange: (questionId: number, optionIds: number[]) => void;
  disabled?: boolean;
}

export const StudentAssignmentTestForm: React.FC<StudentAssignmentTestProps> = ({
  test,
  selections,
  onChange,
  disabled = false,
}) => (
  <TestStack>
    {test.questions.map((question, index) => {
      const selected = selections[question.id] || [];
      return (
        <Question key={question.id}>
          <QuestionMeta>
            Вопрос {index + 1} · {question.question_type === "single_choice" ? "один ответ" : "несколько ответов"}
          </QuestionMeta>
          <QuestionText>{question.text}</QuestionText>
          {question.comment ? <QuestionComment>{question.comment}</QuestionComment> : null}
          {question.attachment ? (
            <AttachmentLink href={question.attachment.url} target="_blank" rel="noreferrer">
              {question.attachment.name}
            </AttachmentLink>
          ) : null}
          <Choices>
            {question.options.map((option) => {
              const isSelected = selected.includes(option.id);
              return (
                <Choice key={option.id} $selected={isSelected}>
                  <ChoiceControl
                    type={question.question_type === "single_choice" ? "radio" : "checkbox"}
                    name={`student-test-question-${question.id}`}
                    checked={isSelected}
                    disabled={disabled}
                    onChange={() => {
                      if (question.question_type === "single_choice") {
                        onChange(question.id, [option.id]);
                        return;
                      }
                      onChange(
                        question.id,
                        isSelected
                          ? selected.filter((id) => id !== option.id)
                          : [...selected, option.id],
                      );
                    }}
                  />
                  <span>{option.text}</span>
                </Choice>
              );
            })}
          </Choices>
        </Question>
      );
    })}
  </TestStack>
);

export const StudentAssignmentTestResult: React.FC<{ attempt: StudentTestAttempt }> = ({ attempt }) => (
  <div>
    <ResultSummary>
      {attempt.percent !== null ? <ResultPercent>{attempt.percent}%</ResultPercent> : null}
      <ResultDetails>
        {attempt.correct_count === null ? "Тест завершён. Ответы отправлены школе без автоматической оценки." : `${attempt.correct_count} из ${attempt.graded_count ?? attempt.question_count} верно`}
        {attempt.graded_count !== undefined && attempt.graded_count > 0 && attempt.graded_count < attempt.question_count ? ` · Без автопроверки: ${attempt.question_count - attempt.graded_count}` : ""}
      </ResultDetails>
    </ResultSummary>
    <TestStack>
      {attempt.snapshot.questions.map((question, index) => {
        const selectedOptions = question.options.filter((option) => option.selected);
        return (
          <Question key={`${attempt.id}-${question.question_id}`}>
            <QuestionMeta>Вопрос {index + 1}</QuestionMeta>
            <QuestionText>{question.text}</QuestionText>
            {question.is_correct === null ? <QuestionComment>Без автоматической оценки</QuestionComment> : <ResultState $correct={question.is_correct}>
              {question.is_correct ? "Верно" : "Неверно"}
            </ResultState>}
            <SelectedAnswer>
              Ваш ответ: {selectedOptions.map((option) => option.text).join(", ") || "не выбран"}
            </SelectedAnswer>
          </Question>
        );
      })}
    </TestStack>
  </div>
);
